import React, { useEffect, useMemo, useState } from 'react';
import PenumbraChart, { HoverPayload, PenumbraOutcome } from './PenumbraChart';
import { useViewport } from '../lib/useViewport';
import { oracleClassTooltip, settlementWindowCopy } from '../lib/oracleClassLabels';

// MarketFocusModal — v2.2 "Dual-State Terminal" Layer 2.
//
// Opens when a MarketFeedRow is clicked. Composes three things:
//   1. The Penumbra Chart (dual-ring: inner = Price, outer = Vibe).
//   2. A metadata strip (resolution hierarchy, vouch count, creator).
//   3. A footer slot rendered via `children` — MultiPage supplies the
//      tier-gated pledge form + OraclePanel so the pledging logic stays
//      in one place (no state duplication).
//
// The modal closes on ESC, backdrop click, or the ✕ button. The
// parent keeps the focused market id in state and unmounts this
// component when it becomes null — no persistence across markets.

export interface MarketFocusMetadata {
  resolutionHierarchy?: string; // e.g. "Human Oracle → Dispute → Sovereign"
  vouchCount?:          number;
  creatorId?:           string;
  status?:              string;
  // I-068 Brief 2 — oracle binding. The modal receives the EFFECTIVE
  // holdback (the per-market override if present, otherwise the
  // registry default) so the explainer copy doesn't need to repeat
  // the merge logic. oracleClass is the raw integer so we can drive
  // tooltip / settlement-window phrasing per class.
  oracleDisplayText?:   string;   // e.g. "Bank of England"
  oracleClassLabel?:    string;   // e.g. "canonical source"
  oracleClass?:         number;   // raw 1..4 for tooltip + window copy
  holdbackHours?:       number;   // effective holdback (override or default)
}

interface Props {
  open:       boolean;
  onClose:    () => void;
  title:      string;
  lens?:      string;
  outcomes:   PenumbraOutcome[];
  metadata?:  MarketFocusMetadata;
  // I-065 — when set to the winning outcome's label (e.g.
  // "Eastern European country"), the PenumbraChart inside dims
  // non-winning slices and highlights the winner with a ✓ glyph.
  // Pass null/undefined for unsettled markets.
  winningOutcomeLabel?: string | null;
  children?:  React.ReactNode;
}

export default function MarketFocusModal({
  open, onClose, title, lens, outcomes, metadata,
  winningOutcomeLabel = null, children,
}: Props): React.JSX.Element | null {
  const [hover, setHover] = useState<HoverPayload>(null);
  const { isMobile } = useViewport();

  // Mobile chart size — the Focus Modal goes full-screen and stacks
  // vertically, with the Penumbra occupying ~40 % of the viewport
  // height. Bounded by min(92vw, 0.4vh-equiv) so it never clips on
  // short phones or narrow ones. Desktop keeps the fixed 340 px.
  const [chartSize, setChartSize] = useState<number>(340);
  useEffect(() => {
    if (!isMobile) { setChartSize(340); return; }
    const compute = (): void => {
      const vw = typeof window !== 'undefined' ? window.innerWidth  : 360;
      const vh = typeof window !== 'undefined' ? window.innerHeight : 640;
      const byHeight = Math.floor(vh * 0.40);
      const byWidth  = Math.floor(vw * 0.82);
      setChartSize(Math.max(200, Math.min(byHeight, byWidth, 320)));
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('orientationchange', compute);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('orientationchange', compute);
    };
  }, [isMobile]);

  // ESC closes the modal. Registered only while open so the listener
  // doesn't fight SystemBriefingSidebar's own ESC handler when the
  // modal isn't visible.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll while open so the underlying terminal doesn't
  // drift behind the backdrop on trackpad scroll. Restored on close.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Hovered-outcome subtitle for the chart centre.
  const centerTop = useMemo(() => {
    if (!outcomes.length) return title;
    return title;
  }, [title, outcomes.length]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Market focus · ${title}`}
      onClick={onClose}
      style={{
        position:       'fixed',
        inset:          0,
        zIndex:         2147483000,
        background:     isMobile ? '#02060f' : 'rgba(2,6,23,0.72)',
        backdropFilter: isMobile ? undefined : 'blur(4px)',
        display:        'flex',
        alignItems:     isMobile ? 'stretch' : 'center',
        justifyContent: 'center',
        padding:        isMobile ? 0 : '56px 16px 24px',
        overflowY:      'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width:        isMobile ? '100%' : 'min(960px, 100%)',
          height:       isMobile ? '100vh' : undefined,
          maxHeight:    isMobile ? '100vh' : 'calc(100vh - 80px)',
          background:   '#0a121d',
          border:       isMobile ? 'none' : '1px solid #1e293b',
          borderRadius: isMobile ? 0 : 10,
          display:      'flex',
          flexDirection:'column',
          overflow:     'hidden',
          boxShadow:    isMobile ? 'none' : '0 24px 60px rgba(0,0,0,0.6)',
          fontFamily:   'monospace',
        }}
      >
        {/* Header */}
        <div style={{
          display:        'flex',
          alignItems:     'center',
          gap:            12,
          padding:        '12px 16px',
          borderBottom:   '1px solid #1e293b',
          background:     '#07101c',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              color:         '#e2e8f0',
              fontSize:      14,
              fontWeight:    700,
              letterSpacing: '0.02em',
              whiteSpace:    'nowrap',
              overflow:      'hidden',
              textOverflow:  'ellipsis',
            }}>
              {title}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 3 }}>
              {lens && (
                <span style={{
                  color:         '#64748b',
                  fontSize:      8,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                }}>
                  {lens}
                </span>
              )}
              {metadata?.status && (
                <span style={{
                  color:         '#34d399',
                  fontSize:      8,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                }}>
                  · {metadata.status}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close market focus"
            style={{
              background:   'none',
              border:       '1px solid #334155',
              borderRadius: isMobile ? 6 : 5,
              color:        '#94a3b8',
              cursor:       'pointer',
              fontSize:     isMobile ? 18 : 13,
              padding:      isMobile ? 0 : '4px 10px',
              width:        isMobile ? 40 : undefined,
              height:       isMobile ? 40 : undefined,
              minWidth:     isMobile ? 40 : undefined,
              lineHeight:   1,
              flexShrink:   0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body scroll region */}
        <div style={{
          flex:            1,
          minHeight:       0,
          overflowY:       'auto',
          overflowX:       'hidden',
          padding:         isMobile ? '12px 14px 20px' : '16px 20px 20px',
          display:         'flex',
          flexDirection:   'column',
          gap:             isMobile ? 14 : 16,
        }}>

          {/* Penumbra Chart — centred. Hover state drives the centre
              copy: default shows the market title + "PRICE · VIBE",
              hovered slice shows outcome + price + ring tag. On mobile
              the chart is sized to ~40 % of viewport height and runs
              the FULL Penumbra-Pulse (the Glow-Only variant lives in
              TacticalMarketCard for the feed). */}
          <div style={{
            display:        'flex',
            justifyContent: 'center',
            alignItems:     'center',
            minHeight:      isMobile ? chartSize + 12 : undefined,
          }}>
            <PenumbraChart
              outcomes={outcomes}
              size={chartSize}
              onHover={setHover}
              centerTop={centerTop}
              centerBottom={hover ? undefined : 'PRICE · VIBE'}
              winningOutcomeLabel={winningOutcomeLabel}
            />
          </div>

          {/* Metadata strip: resolution hierarchy · vouch count · creator */}
          {metadata && (
            <div style={{
              display:         'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap:             10,
              padding:         '10px 12px',
              background:      '#07101c',
              border:          '1px solid #1e293b',
              borderRadius:    6,
            }}>
              {metadata.oracleDisplayText && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em' }}>
                    RESOLUTION
                  </div>
                  <div style={{ color: '#cbd5e1', fontSize: 11, marginTop: 3 }}>
                    {metadata.oracleDisplayText}
                    {metadata.oracleClassLabel && (
                      <>
                        {' · '}
                        <span
                          title={oracleClassTooltip(metadata.oracleClass)}
                          style={{
                            color:           '#a5b4fc',
                            cursor:          'help',
                            borderBottom:    '1px dotted #475569',
                            paddingBottom:   1,
                          }}
                        >
                          {metadata.oracleClassLabel}
                        </span>
                      </>
                    )}
                  </div>
                  {typeof metadata.holdbackHours === 'number' && (
                    <div style={{ color: '#64748b', fontSize: 10, marginTop: 4, lineHeight: 1.5 }}>
                      {settlementWindowCopy(metadata.oracleClass, metadata.holdbackHours)}
                    </div>
                  )}
                </div>
              )}
              {metadata.resolutionHierarchy && (
                <div>
                  <div style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em' }}>
                    RESOLUTION HIERARCHY
                  </div>
                  <div style={{ color: '#cbd5e1', fontSize: 11, marginTop: 3 }}>
                    {metadata.resolutionHierarchy}
                  </div>
                </div>
              )}
              {typeof metadata.vouchCount === 'number' && (
                <div>
                  <div style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em' }}>
                    VOUCHES
                  </div>
                  <div style={{ color: '#cbd5e1', fontSize: 11, marginTop: 3 }}>
                    {metadata.vouchCount}
                  </div>
                </div>
              )}
              {metadata.creatorId && (
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em' }}>
                    MINTED BY
                  </div>
                  <div style={{
                    color:         '#cbd5e1',
                    fontSize:      11,
                    marginTop:     3,
                    whiteSpace:    'nowrap',
                    overflow:      'hidden',
                    textOverflow:  'ellipsis',
                  }}>
                    {metadata.creatorId}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Composed execution surface — pledging + vibe panel
              supplied by the parent so state ownership isn't split. */}
          {children && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
